import React, { useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import { GET_DRIVE_LEGS } from "../../../../utils/GraphQL/queries";
import { useDriveTimePins } from "../../../../utils/DriveTimePin/DriveTimePinContext";
import {
  buildRouteEmbedUrl,
  formatMinutes,
  legSummary,
  totalSeconds,
} from "../../../../utils/DriveTimePin/driveTimeFormat";
import "./driveTime.css";

/**
 * The compare dialog (2026-10-09): the route map with the pins and the target
 * on it, then one row per leg and a total when both pins are set.
 *
 * `layer` is the host's: opened from the calendar's event popup (which sits
 * above the calendar modal) it must be `raised`; from the client rail or the
 * Tasks console, `base`.
 */
const LegRow = ({ label, leg, pending, className = "" }) => (
  <div className={`dt-leg ${className}`.trim()}>
    <span>{label}</span>
    <span className={`dt-leg-value${pending ? " dt-leg-value--pending" : ""}`}>
      {pending ? "…" : legSummary(leg) || "—"}
    </span>
  </div>
);

/*
 * `demoLegs` (walkthrough, registry §17.10): canned legs — the dialog shows
 * them and never calls getDriveLegs, so a tour spends no Google elements and
 * reads the same every time. The map still draws the real route.
 */
const DriveTimeModal = ({ open, onClose, target, layer = "base", demoLegs }) => {
  const pins = useDriveTimePins();
  const [getLegs, { data, loading, error }] = useLazyQuery(GET_DRIVE_LEGS, {
    fetchPolicy: "network-only",
  });

  const startAddr = pins.start?.address || null;
  const endAddr = pins.end?.address || null;
  const targetAddr = target?.address || null;
  // Keyed on the address TEXT, not the objects: a parent re-render hands down
  // a new object for the same place and must not refetch.
  const fetchKey = JSON.stringify([startAddr, endAddr, targetAddr]);

  useEffect(() => {
    if (demoLegs) return;
    if (!open || !targetAddr || (!startAddr && !endAddr)) return;
    getLegs({ variables: { start: startAddr, end: endAddr, stops: [targetAddr] } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fetchKey, !!demoLegs]);

  if (!open) return null;

  const result = demoLegs ? { stops: [demoLegs], budgetExhausted: false } : data?.getDriveLegs;
  const legs = result?.stops?.[0] || null;
  const pending = demoLegs ? false : loading || (!result && !error);
  const embed = buildRouteEmbedUrl({ start: startAddr, target: targetAddr, end: endAddr });
  const both = !!(pins.start && pins.end);
  const total = both && legs ? totalSeconds([legs.fromStart, legs.toEnd]) : null;

  return (
    <BdrModal
      open
      onClose={onClose}
      variant="panel"
      size="md"
      title="Drive time"
      layer={layer}
      /* A walkthrough can open this (tours/tourModals.js); onClose only sets
         state, so the shell tag is safe. */
      tourTag="drive-time-compare"
      actions={<BdrButton onClick={onClose}>Close</BdrButton>}
    >
      <div className="dt-route" aria-label="Route">
        {pins.start && <span className="dt-route-stop">{pins.start.label}</span>}
        {pins.start && <span className="dt-chip-arrow">→</span>}
        <span className="dt-route-stop dt-route-stop--target">{target?.label}</span>
        {pins.end && <span className="dt-chip-arrow">→</span>}
        {pins.end && <span className="dt-route-stop">{pins.end.label}</span>}
      </div>

      {embed && (
        <iframe className="dt-map" src={embed} title="Route map" loading="lazy" />
      )}

      <div className="dt-legs" data-tour="drive-time-legs">
        {pins.start && (
          <LegRow
            label={`${pins.start.label} → ${target?.label}`}
            leg={legs?.fromStart}
            pending={pending}
          />
        )}
        {pins.end && (
          <LegRow
            label={`${target?.label} → ${pins.end.label}`}
            leg={legs?.toEnd}
            pending={pending}
          />
        )}
        {both && (
          <div className="dt-leg dt-leg--total">
            <span>Total driving</span>
            <span className="dt-leg-value">
              {pending ? "…" : total != null ? formatMinutes(total) : "—"}
            </span>
          </div>
        )}
      </div>

      {result?.budgetExhausted && (
        <div className="bdr-alert bdr-alert--warning" role="status">
          Today's drive-time lookups are used up. The map still shows the route; the
          numbers come back tomorrow.
        </div>
      )}
      {error && (
        <div className="bdr-alert bdr-alert--danger" role="alert">
          Couldn't get drive times. {error.message}
        </div>
      )}

      <p className="dt-note">Typical drive time without traffic.</p>
    </BdrModal>
  );
};

export default DriveTimeModal;
